import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ROLES } from '../constants/roles';
import { useModuleViewAccess } from '../hooks/useModuleViewAccess';
import ReceivingModule from './store/ReceivingModule';
import WorkflowGuide from './common/WorkflowGuide';

const ACCESS_ROLES = [
  ROLES.STORE_EXECUTIVE,
  ROLES.STORE_INSPECTOR,
  ROLES.CA_MANAGER,
  ROLES.MRO_MANAGER,
  ROLES.CAMO_QM_MANAGER,
  ROLES.PLANNING_MANAGER,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.SUPER_ADMIN
];

const RECEIVING_TYPE_OPTIONS = [
  {
    value: 'AGAINST_PO',
    label: 'Against PO',
    description: 'Receive supplier stock against an issued purchase order.'
  },
  {
    value: 'LOAN_RENTAL',
    label: 'Loan/Rental',
    description: 'Borrow into store (loan-in) or process returns via linked loan reference.'
  },
  {
    value: 'FROM_AIRCRAFT',
    label: 'From Aircraft',
    description: 'Receive parts removed from a specific aircraft tail into inventory.'
  }
];

const RECEIVING_TYPE_STORAGE_KEY = 'receivingAndInspection.receivingType';
const VALID_RECEIVING_TYPES = new Set(RECEIVING_TYPE_OPTIONS.map((o) => o.value));

function readStoredReceivingType() {
  try {
    const stored = String(sessionStorage.getItem(RECEIVING_TYPE_STORAGE_KEY) || '').trim();
    return VALID_RECEIVING_TYPES.has(stored) ? stored : '';
  } catch {
    return '';
  }
}

function writeStoredReceivingType(value) {
  try {
    const next = String(value || '').trim();
    if (VALID_RECEIVING_TYPES.has(next)) {
      sessionStorage.setItem(RECEIVING_TYPE_STORAGE_KEY, next);
    } else {
      sessionStorage.removeItem(RECEIVING_TYPE_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

const ReceivingAndInspectionPage = () => {
  const location = useLocation();
  const { hasAccess } = useModuleViewAccess('receiving-and-inspection', ACCESS_ROLES);
  const [receivingType, setReceivingType] = useState(
    () => location.state?.receivingType || readStoredReceivingType() || ''
  );
  const loanReceivingPrefill = receivingType === 'LOAN_RENTAL' ? location.state?.loanReceivingPrefill : null;

  const loanReturnPurpose = loanReceivingPrefill?.loanReceivingPurpose || '';

  useEffect(() => {
    if (location.state?.receivingType) {
      setReceivingType(location.state.receivingType);
      return;
    }
    // Loan/workflow deep-links without receivingType still imply Loan/Rental.
    if (location.state?.loanReceivingPrefill) {
      setReceivingType('LOAN_RENTAL');
      return;
    }
    // Bare re-entry via nav must keep the last workflow so history + PRI tag
    // downloads remain available after visiting other modules.
  }, [
    location.key,
    location.state?.receivingType,
    location.state?.loanReceivingPrefill,
    location.state?.receivingWorkflowFilter
  ]);

  useEffect(() => {
    writeStoredReceivingType(receivingType);
  }, [receivingType]);

  const selectedOption = useMemo(
    () => RECEIVING_TYPE_OPTIONS.find((o) => o.value === receivingType),
    [receivingType]
  );

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          You do not have permission to access Receiving and Inspection.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Receiving and Inspection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a receiving workflow. Each type has its own form and history—PO receipts, loan activity, and
        aircraft removals are kept separate.
      </Typography>
      <WorkflowGuide variant="receivingAndInspection" />

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Receiving type
        </Typography>
        <FormControl fullWidth sx={{ maxWidth: 420 }}>
          <Select
            id="receiving-type"
            value={receivingType}
            onChange={(e) => setReceivingType(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Receiving type' }}
            renderValue={(selected) => {
              const opt = RECEIVING_TYPE_OPTIONS.find((o) => o.value === selected);
              if (opt) return opt.label;
              return <em>Select workflow</em>;
            }}
          >
            <MenuItem value="">
              <em>Select workflow</em>
            </MenuItem>
            {RECEIVING_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedOption && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: 560 }}>
            {selectedOption.description}
          </Typography>
        )}
      </Paper>

      {receivingType && (
        <Box sx={{ mt: 2 }}>
          <ReceivingModule
            key={`${receivingType}-${loanReceivingPrefill?.linkedSourceRecordId || 'new'}-${loanReturnPurpose}`}
            receivingContext={receivingType}
            initialLoanPrefill={loanReceivingPrefill}
            recordType={location.state?.recordType === 'receiving' ? 'receiving' : undefined}
            recordId={location.state?.recordType === 'receiving' ? location.state?.recordId : undefined}
          />
        </Box>
      )}
    </Box>
  );
};

export default ReceivingAndInspectionPage;
